import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtDate, fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const WRITE_ROLES = ["ORG_ADMIN", "STORES", "PROCUREMENT"];

export default function Dispatches() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const { companies } = useCompany() || {};
  const [dispatches, setDispatches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: w }, { data: it }, { data: pr }] = await Promise.all([
        api.get("/production-dispatches"), api.get("/inventory/warehouses"), api.get("/inventory/items"), api.get("/projects"),
      ]);
      setDispatches(d); setWarehouses(w); setItems(it); setProjects(pr);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const itemLabel = (id) => { const it = items.find((i) => i.id === id); return it ? `${it.code} — ${it.name}` : "—"; };
  const warehouseName = (id) => warehouses.find((w) => w.id === id)?.name || "—";
  const projectLabel = (id) => { const p = projects.find((x) => x.id === id); return p ? `${p.code} — ${p.name}` : "—"; };

  return (
    <div className="space-y-6" data-testid="dispatches-page">
      <PageHeader
        title="Production Dispatches"
        description="Finished goods leaving a warehouse — issued at the weighted-average unit cost from stock."
        action={canWrite && <CreateDispatchDialog companies={companies} warehouses={warehouses} items={items} projects={projects} onCreated={load} />}
      />

      <div className="rounded-xl border border-border bg-card">
        {!loading && dispatches.length === 0 ? (
          <EmptyState title="No dispatches yet" testId="dispatches-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Number</TableHead><TableHead>Date</TableHead><TableHead>Item</TableHead><TableHead>Warehouse</TableHead><TableHead>Client / Project</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Total cost</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {dispatches.map((d) => (
                <TableRow key={d.id} data-testid={`dispatch-row-${d.number}`}>
                  <TableCell className="font-mono text-xs text-foreground">{d.number}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(d.dispatch_date)}</TableCell>
                  <TableCell className="text-xs text-foreground">{itemLabel(d.item_id)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{warehouseName(d.warehouse_id)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{d.client_name || (d.project_id ? projectLabel(d.project_id) : "—")}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-foreground">{fmtNum(d.quantity, 3)}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{fmtNum(d.total_cost, 2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateDispatchDialog({ companies, warehouses, items, projects, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", warehouse_id: "", item_id: "", project_id: "", client_name: "", dispatch_date: todayISO(), quantity: "", vehicle_no: "" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/production-dispatches", { ...form, project_id: form.project_id || null, client_name: form.client_name || null, vehicle_no: form.vehicle_no || null });
      toast.success("Dispatch created and stock issued");
      setForm({ company_id: "", warehouse_id: "", item_id: "", project_id: "", client_name: "", dispatch_date: todayISO(), quantity: "", vehicle_no: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-dispatch-button"><Plus className="h-4 w-4" /> New Dispatch</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Production Dispatch</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-dispatch-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="dispatch-company-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Warehouse</Label>
              <Select required value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="dispatch-warehouse-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Item</Label>
            <Select required value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="dispatch-item-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} — {i.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Client name (optional)</Label><Input value={form.client_name} onChange={set("client_name")} className="mt-1.5" data-testid="dispatch-client-input" /></div>
            <div>
              <Label>Project (optional)</Label>
              <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="dispatch-project-select"><SelectValue placeholder="Not linked" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not linked</SelectItem>
                  {(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Date</Label><Input required type="date" value={form.dispatch_date} onChange={set("dispatch_date")} className="mt-1.5" data-testid="dispatch-date-input" /></div>
            <div><Label>Quantity</Label><Input required type="number" step="0.001" value={form.quantity} onChange={set("quantity")} className="mt-1.5" data-testid="dispatch-quantity-input" /></div>
            <div><Label>Vehicle no.</Label><Input value={form.vehicle_no} onChange={set("vehicle_no")} className="mt-1.5" data-testid="dispatch-vehicle-input" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-dispatch-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
